import React from 'react';

/* ── Shared label / help node builders ── */
function FieldLabel({ title, isRequired }) {
  return (
    <label className="ob-field-label">
      {title}
      {isRequired && <span className="ob-required">*</span>}
    </label>
  );
}

function FieldHelp({ description }) {
  if (!description) return null;
  return <p className="ob-field-help">{description}</p>;
}

/* ── File upload cloud icon ── */
function UploadIcon() {
  return (
    <svg className="ob-file-zone-icon" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M32 32l-8-8-8 8M24 24v18"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M40.9 36.6A10 10 0 0036 18h-1.26A16 16 0 108 34.5"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── File document icon (shown after upload) ── */
function DocIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10 2H4a1 1 0 00-1 1v12a1 1 0 001 1h10a1 1 0 001-1V7l-5-5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M10 2v5h5M6 10h6M6 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

/* ══════════════════════════════════════════
   Main QuestionField Component
══════════════════════════════════════════ */
function QuestionField({ q, value, onChange, fileMeta, onFileChange, hasError, uploading }) {
  const { id, _id, title, description, inputType, isRequired, options } = q;
  const fieldId = id || _id;
  const errorClass = hasError ? ' ob-input-error' : '';

  /* ── Textarea ── */
  if (inputType === 'textarea') {
    return (
      <div className="ob-field">
        <FieldLabel title={title} isRequired={isRequired} />
        <FieldHelp description={description} />
        <textarea
          id={fieldId}
          className={`ob-textarea${errorClass}`}
          rows={4}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer here..."
        />
      </div>
    );
  }

  /* ── Text-like inputs (email, phone, date, number, text) ── */
  if (['email', 'phone', 'date', 'number', 'text'].includes(inputType) || !inputType) {
    const inputType_ = inputType === 'phone' ? 'tel' : (inputType || 'text');
    const placeholders = {
      email: 'you@example.com',
      tel: '+44 0000 000000',
      date: '',
      number: '0',
      text: 'Type your answer...',
    };
    return (
      <div className="ob-field">
        <FieldLabel title={title} isRequired={isRequired} />
        <FieldHelp description={description} />
        <input
          id={fieldId}
          type={inputType_}
          className={`ob-input${errorClass}`}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholders[inputType_] || 'Type your answer...'}
        />
      </div>
    );
  }

  /* ── Radio cards ── */
  if (inputType === 'radio') {
    return (
      <div className="ob-field">
        <FieldLabel title={title} isRequired={isRequired} />
        <FieldHelp description={description} />
        <div className="ob-options-grid">
          {(options || []).map((opt) => (
            <div key={opt.value} className="ob-option-item">
              <input
                type="radio"
                id={`${fieldId}-${opt.value}`}
                name={fieldId}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <label htmlFor={`${fieldId}-${opt.value}`} className="ob-option-label">
                <span className="ob-option-indicator" />
                {opt.label}
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  }

  /* ── Checkbox cards ── */
  if (inputType === 'checkbox') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="ob-field">
        <FieldLabel title={title} isRequired={isRequired} />
        <FieldHelp description={description} />
        <div className="ob-options-grid">
          {(options || []).map((opt) => {
            const checked = arr.includes(opt.value);
            return (
              <div key={opt.value} className="ob-option-item">
                <input
                  type="checkbox"
                  id={`${fieldId}-${opt.value}`}
                  value={opt.value}
                  checked={checked}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? [...arr, opt.value]
                        : arr.filter((v) => v !== opt.value)
                    )
                  }
                />
                <label htmlFor={`${fieldId}-${opt.value}`} className="ob-option-label">
                  <span className="ob-option-indicator square" />
                  {opt.label}
                </label>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── Select ── */
  if (inputType === 'select') {
    return (
      <div className="ob-field">
        <FieldLabel title={title} isRequired={isRequired} />
        <FieldHelp description={description} />
        <div className="ob-select-wrap">
          <select
            id={fieldId}
            className={`ob-select${errorClass}`}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value="">Select an option...</option>
            {(options || []).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  /* ── File upload ── */
  if (inputType === 'file') {
    const fileName = fileMeta?.name || fileMeta?.fileName;
    const fileUrl = fileMeta?.fileUrl;

    return (
      <div className="ob-field">
        <FieldLabel title={title} isRequired={isRequired} />
        <FieldHelp description={description} />

        {/* Uploading spinner */}
        {uploading && !fileUrl && (
          <div className="ob-file-uploading">
            <span className="ob-spinner" />
            Uploading file...
          </div>
        )}

        {/* Uploaded file card */}
        {!uploading && fileUrl && (
          <div className="ob-file-card">
            <div className="ob-file-card-icon">
              <DocIcon />
            </div>
            <span className="ob-file-card-name">{fileName || 'Uploaded file'}</span>
            <button
              type="button"
              className="ob-file-card-remove"
              onClick={() => onFileChange(null)}
              aria-label="Remove file"
            >
              ×
            </button>
          </div>
        )}

        {/* Drop zone (only shown when no file) */}
        {!uploading && !fileUrl && (
          <div className={`ob-file-zone${hasError ? ' ob-input-error' : ''}`}>
            <input
              id={fieldId}
              type="file"
              onChange={(e) => {
                if (e.target.files?.[0]) onFileChange(e.target.files[0]);
              }}
            />
            <UploadIcon />
            <p className="ob-file-zone-title">
              Drag &amp; drop or <b>browse</b>
            </p>
            <p className="ob-file-zone-hint">Maximum file size: 5 MB</p>
          </div>
        )}
      </div>
    );
  }

  /* ── Default: text fallback ── */
  return (
    <div className="ob-field">
      <FieldLabel title={title} isRequired={isRequired} />
      <FieldHelp description={description} />
      <input
        id={fieldId}
        type="text"
        className={`ob-input${errorClass}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type your answer..."
      />
    </div>
  );
}

export default QuestionField;
